import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { ChevronDown, LogOut, User as UserIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { auth as authApi, license as licenseApi, type LicenseStatus } from "@/lib/api";
import { AUTH_MODE, useAuth, _setAccessToken } from "@/lib/auth";

/**
 * Topbar user pill — shows the signed-in user's email and a dropdown with
 * Sign out. Visible on every authenticated page.
 */
export function UserMenu() {
  const meQuery = useQuery({
    queryKey: ["me"],
    queryFn: () => authApi.me(),
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
  const licenseQuery = useQuery({
    queryKey: ["license-status"],
    queryFn: () => licenseApi.status(),
    retry: false,
    refetchOnWindowFocus: false,
    // Refresh hourly so grace/expiration changes surface without a full reload.
    staleTime: 60 * 60 * 1000,
    enabled: Boolean(meQuery.isSuccess),
  });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const cognito = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const handleSignOut = async () => {
    try {
      // 1. If Cognito SRP is in use, clear the SDK + our cached tokens.
      if (AUTH_MODE === "cognito") {
        const m = await import("@/lib/cognito-srp");
        m.srpSignOut();
      }
      // 2. Always clear the module-level Bearer so the next request goes out
      //    without one.
      _setAccessToken(null);
      // 3. Always hit /auth/logout — kills the legacy session cookie if any.
      //    Crucial because `require_user` falls back to the cookie when no
      //    Bearer is present; otherwise sign-out leaks a previous local-mode
      //    session.
      await authApi.logout().catch(() => undefined);
      // 4. Sweep any orphaned OIDC PKCE state left over from incomplete
      //    Hosted UI redirects. These are short-lived CSRF state but they
      //    look messy in localStorage on the next page load.
      try {
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const k = localStorage.key(i);
          if (k && k.startsWith("oidc.")) localStorage.removeItem(k);
        }
      } catch {
        /* swallow */
      }
    } finally {
      // 4. Drop the cached `me` query and the entire query cache (workspaces,
      //    entities, signals, etc.) so the next user doesn't see the previous
      //    user's data when they log back in.
      qc.removeQueries({ queryKey: ["me"] });
      qc.clear();
      // 5. Hard-redirect to the login page. Using window.location forces a
      //    full unmount of every provider, which also nukes any in-memory
      //    auth state that could otherwise linger.
      window.location.href = "/";
    }
  };

  if (!meQuery.isSuccess) return null;
  const email = meQuery.data.email ?? "user";
  const initials = (email[0] ?? "?").toUpperCase();

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-md border border-border bg-surface px-2.5 py-1.5 text-[12px] hover:bg-surface-elevated transition"
      >
        <span className="grid h-5 w-5 place-items-center rounded-full bg-primary/20 text-primary text-[10px] font-semibold">
          {initials}
        </span>
        <span className="hidden lg:inline text-foreground/90 truncate max-w-[140px]">
          {email}
        </span>
        <ChevronDown className="h-3 w-3 opacity-60" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 w-[240px] rounded-md border border-border bg-popover shadow-xl overflow-hidden">
          <div className="px-3 py-2.5 border-b border-border">
            <div className="flex items-center justify-between gap-2">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Signed in as
              </div>
              {meQuery.data.is_dravian_staff && (
                <span
                  className="text-[9px] uppercase tracking-wider text-primary border border-primary/40 bg-primary/10 px-1.5 py-0.5 rounded"
                  title="Your account also belongs to Dravian internal staff"
                >
                  Staff
                </span>
              )}
            </div>
            <div className="text-[12.5px] font-medium text-foreground truncate">
              {email}
            </div>
            <div className="text-[10px] text-muted-foreground mt-0.5 font-mono">
              {meQuery.data.role ?? "analyst"}
              {cognito.mode === "cognito" && " · via Cognito"}
            </div>
          </div>
          {licenseQuery.data && (
            <div className="px-3 py-2 border-b border-border bg-surface-elevated/30">
              <LicenseLine status={licenseQuery.data} />
            </div>
          )}
          <button
            onClick={() => {
              setOpen(false);
              navigate({ to: "/settings" });
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-[12px] text-foreground/85 hover:bg-surface-elevated"
          >
            <UserIcon className="h-3.5 w-3.5" /> Account settings
          </button>
          <button
            onClick={handleSignOut}
            className="w-full flex items-center gap-2 px-3 py-2 text-[12px] text-foreground/85 hover:bg-risk-high/10 hover:text-risk-high border-t border-border"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function LicenseLine({ status }: { status: LicenseStatus }) {
  const expiresIso = status.expires_at;
  const daysToExpire = expiresIso
    ? Math.round(
        (new Date(expiresIso).getTime() - Date.now()) / (24 * 3600 * 1000),
      )
    : null;

  const tone = (() => {
    if (status.state === "needs_activation") return "text-risk-high";
    if (status.state === "degraded") return "text-risk-high";
    if (status.state === "grace") return "text-risk-medium";
    if (daysToExpire !== null && daysToExpire < 14) return "text-risk-medium";
    return "text-muted-foreground";
  })();

  const label = (() => {
    if (status.state === "needs_activation") return "Activation required";
    if (status.state === "degraded") return "License service unreachable";
    if (status.state === "grace") {
      const until = status.grace_until ? new Date(status.grace_until) : null;
      return `Grace period · ends ${until?.toLocaleDateString() ?? "soon"}`;
    }
    if (status.organization_name) {
      const tier = status.subscription_tier ?? "—";
      const exp = daysToExpire !== null ? ` · ${daysToExpire}d to renew` : "";
      return `${status.organization_name} · ${tier}${exp}`;
    }
    return "License active";
  })();

  return (
    <div className={`text-[10px] font-mono ${tone}`}>
      {label}
      {status.message && (
        <div className="mt-0.5 text-muted-foreground italic text-[10px] leading-tight">
          {status.message}
        </div>
      )}
    </div>
  );
}
